import Term from "../Term";

/**
 * Optimized function to chunk speaker term arrays using fullTurns metadata or fixed chunk sizes.
 * Handles large datasets efficiently.
 *
 * @param {Array} speakersArray - Array of speaker objects containing `value` (array of terms).
 * @param {number} chunkSize - Optional fixed chunk size in ms if fullTurns is not provided.
 * @param {Array} fullTurns - Optional array of {speaker, s, e} turns for exact segmentation.
 * @returns {Array} Chunked speaker segments.
 */
export default function chunkSpeakers(speakersArray, chunkSize = 12000, fullTurns = []) {
  if (!Array.isArray(speakersArray) || speakersArray.length === 0) return [];

  const hasFullTurns = Array.isArray(fullTurns) && fullTurns.length;
  const result = [];

  // Pre-index fullTurns by speaker to avoid repeated filtering
  const fullTurnsMap = hasFullTurns
    ? fullTurns.reduce((acc, turn) => {
        const key = turn.speaker || 'Unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(turn);
        return acc;
      }, {})
    : null;

  for (const speaker of speakersArray) {
    const terms = speaker.value;
    if (!Array.isArray(terms) || terms.length === 0) continue;

    const speakerId = speaker.get?.('i') || speaker.get?.('speaker') || 'Unknown';

    if (hasFullTurns) {
      const matchingTurns = [
        ...(fullTurnsMap[speakerId] || []),
        ...(speakerId !== 'Unknown' ? fullTurnsMap['Unknown'] || [] : []),
      ];

      for (let i = 0; i < matchingTurns.length; i++) {
        const { s, e } = matchingTurns[i];

        // Binary search-like approach for large sorted term arrays (assuming sorted)
        const chunkTerms = [];
        for (let j = 0; j < terms.length; j++) {
          const term = terms[j];
          if (term.start >= s && term.end <= e) {
            chunkTerms.push(term);
          }
        }

        if (chunkTerms.length === 0) continue;

        const newSpeaker = i === 0 ? speaker : new Term(speaker);
        newSpeaker.value = chunkTerms;
        if (typeof newSpeaker.freezeWords === 'function') {
          newSpeaker.freezeWords();
        }
        result.push(newSpeaker);
      }
    } else {
      // No fullTurns: fallback to chunkSize approach
      const startTime = terms[0].start;
      const chunksMap = new Map();

      for (const term of terms) {
        const index = Math.floor((term.start - startTime) / chunkSize);
        if (!chunksMap.has(index)) chunksMap.set(index, []);
        chunksMap.get(index).push(term);
      }

      let isFirst = true;
      for (const chunk of chunksMap.values()) {
        const newSpeaker = isFirst ? speaker : new Term(speaker);
        newSpeaker.value = chunk;
        if (typeof newSpeaker.freezeWords === 'function') {
          newSpeaker.freezeWords();
        }
        result.push(newSpeaker);
        isFirst = false;
      }
    }
  }

  return result;
}
